import { useEffect, useId, useRef, useState } from 'react';
import classnames from 'classnames';
import IconClose from '@scramble/design-system/icons/x-close.svg';
import { RequiredMark } from '../RequiredMark';
import { InputFieldProps } from './types';
import s from './index.module.scss';

export const InputField = ({
  id,
  label,
  isRequired = false,
  error,
  success,
  description,
  onClear,
  leftContainer,
  rightContainer,
  className,
  ...nativeInputProps
}: InputFieldProps) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const [focused, setFocused] = useState(false);

  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const [leftWidth, setLeftWidth] = useState(0);
  const [rightWidth, setRightWidth] = useState(0);

  useEffect(() => {
    if (!leftRef.current) return;

    const observer = new ResizeObserver(([entry]) => {
      if (entry) setLeftWidth(entry.contentRect.width);
    });

    observer.observe(leftRef.current);
    return () => observer.disconnect();
  }, [leftContainer]);

  useEffect(() => {
    if (!rightRef.current) return;

    const observer = new ResizeObserver(([entry]) => {
      if (entry) setRightWidth(entry.contentRect.width);
    });

    observer.observe(rightRef.current);
    return () => observer.disconnect();
  }, [rightContainer, onClear]);

  const hasRightSlot = rightContainer != null || onClear != null;

  const inputStyle = {
    ...(leftWidth > 0 ? { paddingLeft: `${leftWidth + 16 + 8}px` } : {}),
    ...(rightWidth > 0 ? { paddingRight: `${rightWidth + 16 + 8}px` } : {}),
  };

  return (
    <div
      className={classnames(
        s.inputField,
        error && s['inputField--error'],
        success && !error && s['inputField--success'],
        className
      )}
    >
      {label && (
        <label className={s.inputField__label} htmlFor={inputId}>
          {label}
          {isRequired && <RequiredMark hasError={!!error} />}
        </label>
      )}

      <div className={s.inputField__wrapper}>
        {leftContainer != null && (
          <div ref={leftRef} className={s.inputField__leftContainer}>
            {leftContainer({ focused })}
          </div>
        )}

        <input
          id={inputId}
          className={s.inputField__input}
          style={inputStyle}
          aria-required={isRequired || undefined}
          {...nativeInputProps}
          onFocus={(e) => {
            setFocused(true);
            nativeInputProps.onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            nativeInputProps.onBlur?.(e);
          }}
        />

        {hasRightSlot && (
          <div ref={rightRef} className={s.inputField__rightContainer}>
            {rightContainer != null
              ? rightContainer({ focused })
              : nativeInputProps.value && (
                  <button
                    type="button"
                    className={s.inputField__clearButton}
                    onClick={onClear}
                    tabIndex={-1}
                    aria-label="Clear"
                  >
                    <img src={IconClose} alt="close" width={16} height={16} />
                  </button>
                )}
          </div>
        )}
      </div>

      {error && (
        <span className={classnames(s.inputField__message, s['inputField__message--error'])}>
          {error}
        </span>
      )}

      {!error && description && <span className={s.inputField__message}>{description}</span>}
    </div>
  );
};
